import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api-client";
import { User, PaginationMeta } from "@/types/api";

type CurrentUserResponse = { data: User };
export const getCurrentUser = (): Promise<{ data: CurrentUserResponse }> => {
  return api.get<CurrentUserResponse>("/api/v1/users/me");
};

export const useCurrentUserApi = ({ manual = false }: { manual?: boolean } = {}) => {
  const query = useQuery({
    queryKey: ["current-user"],
    queryFn: () => getCurrentUser().then((response) => response.data),
    enabled: !manual,
    retry: false,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });
  return { data: query.data, isLoading: query.isLoading, isError: query.isError, fetchCurrentUser: query.refetch };
};

type DesignatedApproverResponse = { isDesignatedApprover: boolean };
export const getIsDesignatedApprover = (): Promise<{ data: DesignatedApproverResponse }> => {
  return api.get<DesignatedApproverResponse>("/api/v1/users/me/designated_approver");
};

export const useIsDesignatedApproverApi = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const query = useQuery({
    queryKey: ["is-designated-approver"],
    queryFn: () => getIsDesignatedApprover().then((response) => response.data),
    enabled,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });
  return { data: query.data, isLoading: query.isLoading, isError: query.isError };
};

type UsersResponse = { data: User[]; meta: PaginationMeta };

export const getLandlordUsers = (
  landlordId: string,
  { page = 1, per_page = 20, searchTerm }: { page?: number; per_page?: number; searchTerm?: string } = {}
): Promise<{ data: UsersResponse }> => {
  return api.get<UsersResponse>(`/api/v1/landlords/${landlordId}/users`, {
    params: { page, per_page, search_term: searchTerm }
  });
};

export const useLandlordUsersApi = ({
  landlordId,
  per_page = 10,
  searchTerm,
  enabled = true
}: {
  landlordId: string;
  per_page?: number;
  searchTerm?: string;
  enabled?: boolean;
}) => {
  const query = useInfiniteQuery({
    queryKey: ["landlord-users", landlordId, searchTerm],
    queryFn: async ({ pageParam = 1 }) => getLandlordUsers(landlordId, { page: pageParam, per_page, searchTerm }),
    getNextPageParam: (lastPage) => {
      if (lastPage.data.meta.currentPage < lastPage.data.meta.totalPages) {
        return lastPage.data.meta.currentPage + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled
  });

  return {
    data: query.data?.pages.flatMap((page) => page.data.data) ?? undefined,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isError: query.isError
  };
};

export const removeUserFromLandlord = (landlordId: string, userId: string) => {
  return api.delete(`/api/v1/landlords/${landlordId}/users/${userId}/remove_from_landlord`);
};

export const useRemoveUserApi = ({ landlordId }: { landlordId: string }) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ userId }: { userId: string }) => removeUserFromLandlord(landlordId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["landlord-users"] });
    }
  });
  return {
    mutate: mutation.mutate,
    isPending: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error
  };
};

export type CreateUserPayload = {
  firstname: string;
  lastname: string;
  email: string;
  password: string;
};

export const createUser = (landlordId: string, user: CreateUserPayload) => {
  return api.post(`/api/v1/landlords/${landlordId}/users`, { data: user }).catch((error) => {
    if (error.response.status === 422) {
      throw new Error(error.response.data.errors.join("\n"));
    }
    throw new Error(error);
  });
};

export const useCreateUserApi = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ landlordId, user }: { landlordId: string; user: CreateUserPayload }) => createUser(landlordId, user),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["landlord-users"] });
    }
  });
  return {
    mutate: mutation.mutate,
    isPending: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error
  };
};

export const addUserToLandlord = (landlordId: string, email: string) => {
  return api.post(`/api/v1/landlords/${landlordId}/users/add_to_landlord`, { data: { email } }).catch((error) => {
    if (error.response.status === 422) {
      throw new Error(error.response.data.errors);
    }
    throw new Error(error);
  });
};

export const useAddUserToLandlordApi = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ landlordId, email }: { landlordId: string; email: string }) => addUserToLandlord(landlordId, email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["landlord-users"] });
    }
  });
  return {
    mutate: mutation.mutate,
    isPending: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error
  };
};

// Assign properties to a user
export const assignPropertiesToUser = (landlordId: string, userId: string, propertyIds: string[]) => {
  return api.post(`/api/v1/landlords/${landlordId}/users/${userId}/assign_properties`, {
    data: { property_ids: propertyIds }
  });
};

export const useAssignPropertiesToUserApi = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ landlordId, userId, propertyIds }: { landlordId: string; userId: string; propertyIds: string[] }) =>
      assignPropertiesToUser(landlordId, userId, propertyIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["landlord-users"] });
    }
  });
  return {
    mutate: mutation.mutate,
    isPending: mutation.isPending,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    error: mutation.error
  };
};
